import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Video, Plus, Trash2, Play, ExternalLink, X, 
  Loader2, ChevronDown, ChevronUp, Link2, Tag,
  Trophy, Target, Shield, Users, Clock, Award,
  Bookmark, SkipForward, Scissors, Copy, Check,
  Sparkles, Camera
} from 'lucide-react';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

// Supported sports for AI analysis
const AI_SUPPORTED_SPORTS = [
  { id: 'football', label: 'Football', icon: '⚽' },
  { id: 'rugby', label: 'Rugby', icon: '🏉' },
  { id: 'basketball', label: 'Basketball', icon: '🏀' },
  { id: 'tennis', label: 'Tennis', icon: '🎾' },
  { id: 'hockey', label: 'Hockey', icon: '🏒' },
  { id: 'generic', label: 'Other Sport', icon: '🏃' }
];

// Platform logos/colors
const PLATFORM_CONFIG = {
  'YouTube': { color: '#FF0000', icon: '▶️', supportsTimestamp: true },
  'VEO': { color: '#00D4AA', icon: '🎬', supportsTimestamp: true },
  'XbotGo': { color: '#4A90D9', icon: '🤖', supportsTimestamp: false },
  'Vimeo': { color: '#1AB7EA', icon: '▶️', supportsTimestamp: true },
  'Hudl': { color: '#FF6B00', icon: '🏈', supportsTimestamp: true },
  'Other': { color: '#71717a', icon: '🔗', supportsTimestamp: false }
};

// Stat type icons
const STAT_ICONS = {
  scoring: '⚽',
  contribution: '🎯',
  defensive: '🛡️',
  attacking: '⚡',
  discipline: '🟨',
  award: '🏆',
  role: '©️',
  appearance: '⏱️',
  achievement: '🥇',
  other: '📝'
};

// Format seconds to MM:SS
const formatTimestamp = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export default function VideoFootage({ childId, childName, canEdit = true }) {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [statTypes, setStatTypes] = useState([]);
  const [showMatchDetails, setShowMatchDetails] = useState(false);
  const iframeRef = useRef(null);
  
  // Timestamp marker state
  const [showMarkerForm, setShowMarkerForm] = useState(false);
  const [newMarker, setNewMarker] = useState({ minutes: '', seconds: '', label: '', stat_type: '', notes: '' });
  const [addingMarker, setAddingMarker] = useState(false);
  
  // Clip export state
  const [showClipsPanel, setShowClipsPanel] = useState(false);
  const [clips, setClips] = useState([]);
  const [loadingClips, setLoadingClips] = useState(false);
  const [copiedClipId, setCopiedClipId] = useState(null);
  
  // AI Analysis state
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [aiSport, setAiSport] = useState('football');
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [aiProgress, setAiProgress] = useState(0);
  const [capturedFrames, setCapturedFrames] = useState([]);
  const [aiResults, setAiResults] = useState(null);
  
  // Form state
  const [newVideo, setNewVideo] = useState({
    title: '',
    url: '',
    description: '',
    date_recorded: '',
    tags: ''
  });
  
  // Match details state
  const [matchDetails, setMatchDetails] = useState({
    opponent: '',
    score_for: '',
    score_against: '',
    result: '',
    venue: '',
    competition: ''
  });
  
  // Player stats state
  const [playerStats, setPlayerStats] = useState([]);
  const [newStat, setNewStat] = useState({ stat_type: '', value: '1', minute: '' });

  const loadVideos = useCallback(async () => {
    try {
      setLoading(true);
      const [videosRes, statsRes] = await Promise.all([
        axios.get(`${API}/training-plan/videos/${childId}`),
        axios.get(`${API}/training-plan/stat-types`)
      ]);
      setVideos(videosRes.data.videos || []);
      setStatTypes(statsRes.data.stat_types || []);
    } catch (error) {
      // Stat types might not load, continue anyway
      try {
        const response = await axios.get(`${API}/training-plan/videos/${childId}`);
        setVideos(response.data.videos || []);
      } catch (err) {
        // Ignore
      }
    } finally {
      setLoading(false);
    }
  }, [childId]);

  useEffect(() => {
    loadVideos();
  }, [loadVideos]);

  const handleAddVideo = async () => {
    if (!newVideo.title.trim() || !newVideo.url.trim()) {
      toast.error('Please enter a title and URL');
      return;
    }

    // Basic URL validation
    if (!newVideo.url.startsWith('http://') && !newVideo.url.startsWith('https://')) {
      toast.error('Please enter a valid URL starting with http:// or https://');
      return;
    }

    setAdding(true);
    try {
      // Build match details if provided
      let matchDetailsObj = null;
      if (showMatchDetails && matchDetails.opponent) {
        matchDetailsObj = {
          opponent: matchDetails.opponent,
          score: matchDetails.score_for && matchDetails.score_against 
            ? `${matchDetails.score_for}-${matchDetails.score_against}` 
            : null,
          result: matchDetails.result || null,
          venue: matchDetails.venue || null,
          competition: matchDetails.competition || null
        };
      }

      const response = await axios.post(`${API}/training-plan/video/${childId}`, {
        title: newVideo.title,
        url: newVideo.url,
        description: newVideo.description,
        date_recorded: newVideo.date_recorded || null,
        tags: newVideo.tags ? newVideo.tags.split(',').map(t => t.trim()) : [],
        match_details: matchDetailsObj,
        player_stats: playerStats.length > 0 ? playerStats : null
      });
      
      toast.success(`Video added! (${response.data.source})`);
      setShowAddForm(false);
      setShowMatchDetails(false);
      setNewVideo({ title: '', url: '', description: '', date_recorded: '', tags: '' });
      setMatchDetails({ opponent: '', score_for: '', score_against: '', result: '', venue: '', competition: '' });
      setPlayerStats([]);
      loadVideos();
    } catch (error) {
      toast.error('Failed to add video');
    } finally {
      setAdding(false);
    }
  };

  const handleAddStat = () => {
    if (!newStat.stat_type) return;
    
    const statInfo = statTypes.find(s => s.id === newStat.stat_type);
    setPlayerStats([...playerStats, {
      stat_type: newStat.stat_type,
      stat_label: statInfo?.label || newStat.stat_type,
      category: statInfo?.category || 'other',
      value: newStat.value || '1',
      minute: newStat.minute || null
    }]);
    setNewStat({ stat_type: '', value: '1', minute: '' });
  };

  const handleRemoveStat = (index) => {
    setPlayerStats(playerStats.filter((_, i) => i !== index));
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm('Delete this video link?')) return;
    
    try {
      await axios.delete(`${API}/training-plan/video/${videoId}`);
      toast.success('Video deleted');
      if (playingVideo?.id === videoId) {
        setPlayingVideo(null);
      }
      loadVideos();
    } catch (error) {
      toast.error('Failed to delete video');
    }
  };

  const handlePlayVideo = (video, startTime = null) => {
    if (video.can_embed) {
      // Build embed URL with timestamp if provided
      let embedUrl = video.embed_url;
      if (startTime !== null && video.source === 'YouTube') {
        // YouTube embed with start time
        const separator = embedUrl.includes('?') ? '&' : '?';
        embedUrl = `${embedUrl}${separator}start=${startTime}&autoplay=1`;
      } else if (startTime !== null && video.source === 'Vimeo') {
        // Vimeo embed with time
        const separator = embedUrl.includes('?') ? '&' : '?';
        embedUrl = `${embedUrl}${separator}#t=${startTime}s`;
      }
      setPlayingVideo({ ...video, embed_url: embedUrl, currentTime: startTime });
    } else {
      // Open in new tab
      window.open(video.url, '_blank', 'noopener,noreferrer');
    }
  };

  // Add timestamp marker
  const handleAddMarker = async () => {
    if (!playingVideo || !newMarker.label) {
      toast.error('Please enter a label for the marker');
      return;
    }

    const timeSeconds = (parseInt(newMarker.minutes) || 0) * 60 + (parseInt(newMarker.seconds) || 0);
    
    if (timeSeconds <= 0) {
      toast.error('Please enter a valid time');
      return;
    }

    setAddingMarker(true);
    try {
      await axios.post(`${API}/training-plan/video/${playingVideo.id}/marker`, {
        time_seconds: timeSeconds,
        label: newMarker.label,
        stat_type: newMarker.stat_type || null,
        notes: newMarker.notes || null
      });
      
      toast.success(`Marker added at ${formatTimestamp(timeSeconds)}`);
      setShowMarkerForm(false);
      setNewMarker({ minutes: '', seconds: '', label: '', stat_type: '', notes: '' });
      
      // Reload videos to get updated markers
      loadVideos();
    } catch (error) {
      toast.error('Failed to add marker');
    } finally {
      setAddingMarker(false);
    }
  };

  // Delete timestamp marker
  const handleDeleteMarker = async (markerId) => {
    if (!playingVideo) return;
    
    try {
      await axios.delete(`${API}/training-plan/video/${playingVideo.id}/marker/${markerId}`);
      toast.success('Marker deleted');
      loadVideos();
    } catch (error) {
      toast.error('Failed to delete marker');
    }
  };

  // Jump to timestamp
  const handleJumpToTimestamp = (timeSeconds) => {
    if (!playingVideo) return;
    handlePlayVideo(playingVideo, timeSeconds);
  };

  // Load video clips
  const handleLoadClips = async () => {
    if (!playingVideo) return;
    
    setLoadingClips(true);
    try {
      const response = await axios.get(`${API}/training-plan/video/${playingVideo.id}/clips?padding_seconds=5`);
      setClips(response.data.clips || []);
      setShowClipsPanel(true);
    } catch (error) {
      toast.error('Failed to load clips');
    } finally {
      setLoadingClips(false);
    }
  };

  // Copy clip URL to clipboard
  const handleCopyClipUrl = async (clip) => {
    if (clip.clip_url) {
      try {
        await navigator.clipboard.writeText(clip.clip_url);
        setCopiedClipId(clip.marker_id);
        toast.success('Clip URL copied!');
        setTimeout(() => setCopiedClipId(null), 2000);
      } catch (error) {
        toast.error('Failed to copy URL');
      }
    }
  };

  // AI Video Analysis - Capture frame from video
  const captureVideoFrame = async (timeSeconds) => {
    if (!playingVideo) return null;
    
    // For embedded videos, we can't directly capture frames
    // Instead, we'll use a placeholder approach where user can upload screenshots
    // or we use video thumbnails from the platform
    
    // Note: Direct frame capture requires the video to be same-origin or CORS-enabled
    // Most embedded videos (YouTube, Vimeo) don't allow this
    return null;
  };

  // AI Analysis - Analyze video with selected sport
  const handleAiAnalysis = async () => {
    if (!playingVideo) return;
    
    // Check if we have any timestamp markers to analyze
    if (!playingVideo.timestamp_markers || playingVideo.timestamp_markers.length === 0) {
      toast.error('Please add some timestamp markers first. AI analysis will use those timestamps to detect events.');
      return;
    }
    
    setAiAnalyzing(true);
    setAiProgress(0);
    setAiResults(null);
    
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      toast.info(`Analyzing ${aiSport} video at ${playingVideo.timestamp_markers.length} marked timestamps...`);
      
      // Use the existing timestamp markers as analysis points
      // Since we can't capture frames from embedded videos due to CORS,
      // we'll use a simplified analysis based on existing markers
      setAiProgress(30);
      
      // Get analysis suggestions based on sport and existing markers
      const response = await axios.post(
        `${API}/video-analysis/analyze-markers`,
        { 
          video_id: playingVideo.id,
          sport: aiSport,
          existing_markers: playingVideo.timestamp_markers.map(m => ({
            time_seconds: m.time_seconds,
            label: m.label,
            stat_type: m.stat_type
          }))
        },
        { headers }
      );
      
      setAiProgress(80);
      
      if (response.data.suggestions?.length > 0) {
        setAiResults({
          ...response.data,
          high_confidence_events: response.data.suggestions.length,
          suggested_markers: response.data.suggestions
        });
        toast.success(`Generated ${response.data.suggestions.length} AI suggestions!`);
      } else {
        toast.info('AI analysis complete. Add more markers for better suggestions.');
      }
      
      setAiProgress(100);
      
    } catch (error) {
      console.error('AI analysis error:', error);
      // Fallback: Generate sport-specific suggestions based on marker labels
      const suggestions = generateSportSuggestions(playingVideo.timestamp_markers, aiSport);
      if (suggestions.length > 0) {
        setAiResults({
          high_confidence_events: suggestions.length,
          suggested_markers: suggestions
        });
        toast.success(`Generated ${suggestions.length} suggestions based on your markers!`);
      } else {
        toast.error('Unable to generate AI suggestions. Try adding more descriptive markers.');
      }
    } finally {
      setAiAnalyzing(false);
    }
  };

  // Helper function to generate sport-specific suggestions
  const generateSportSuggestions = (markers, sport) => {
    const sportEventTypes = {
      football: ['goal', 'shot', 'save', 'tackle', 'pass', 'cross', 'header', 'dribble', 'foul'],
      rugby: ['try', 'conversion', 'tackle', 'ruck', 'maul', 'lineout', 'scrum'],
      basketball: ['basket', 'three_pointer', 'dunk', 'block', 'steal', 'assist'],
      tennis: ['ace', 'winner', 'volley', 'smash', 'rally'],
      hockey: ['goal', 'save', 'assist', 'penalty'],
      generic: ['score', 'save', 'tackle', 'key_moment']
    };
    
    const events = sportEventTypes[sport] || sportEventTypes.generic;
    
    return markers
      .filter(m => {
        const labelLower = m.label.toLowerCase();
        return events.some(e => labelLower.includes(e.replace('_', ' ')));
      })
      .map(m => ({
        time_seconds: m.time_seconds,
        label: m.label,
        stat_type: m.stat_type || 'key_moment',
        notes: `${sport} event detected from marker`
      }));
  };

  // Apply AI-generated markers to video
  const handleApplyAiMarkers = async () => {
    if (!aiResults?.analysis_id || !playingVideo) return;
    
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.post(
        `${API}/video-analysis/auto-tag/${playingVideo.id}`,
        { analysis_id: aiResults.analysis_id },
        { headers }
      );
      
      toast.success(`Added ${response.data.markers_added} AI-generated markers!`);
      setShowAiPanel(false);
      setAiResults(null);
      loadVideos(); // Refresh to show new markers
      
    } catch (error) {
      toast.error('Failed to apply markers');
    }
  };

  const getSourceConfig = (source) => {
    return PLATFORM_CONFIG[source] || PLATFORM_CONFIG['Other'];
  };

  if (loading) {
    return (
      <div className="bg-zinc-950/30 border border-zinc-800 backdrop-blur-sm p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 text-[#02b5ff] animate-spin" />
          <span className="ml-2 text-zinc-400">Loading video footage...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-zinc-950/30 border border-zinc-800 backdrop-blur-sm p-6" data-testid="video-footage-section">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div 
          className="flex items-center gap-3 cursor-pointer"
          onClick={() => setExpanded(!expanded)}
        >
          <Video className="h-6 w-6 text-[#ff025f]" />
          <div>
            <h2 className="text-lg font-bold uppercase tracking-widest text-white">Video Footage</h2>
            <p className="text-xs text-zinc-500 mt-1">
              {videos.length} video{videos.length !== 1 ? 's' : ''} • VEO, XbotGo, YouTube
            </p>
          </div>
          {expanded ? (
            <ChevronUp className="h-5 w-5 text-zinc-500" />
          ) : (
            <ChevronDown className="h-5 w-5 text-zinc-500" />
          )}
        </div>
        
        {canEdit && (
          <Button
            onClick={() => setShowAddForm(!showAddForm)}
            className="h-9 px-4 bg-[#ff025f] hover:bg-[#ff025f]/80 text-white text-xs"
            data-testid="add-video-button"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Video
          </Button>
        )}
      </div>

      {expanded && (
        <>
          {/* Add Video Form */}
          {showAddForm && (
            <div className="mb-6 p-4 bg-zinc-900/50 border border-zinc-700 rounded-lg" data-testid="add-video-form">
              <h3 className="text-sm font-bold text-zinc-400 uppercase mb-4">Add Video Link</h3>
              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-zinc-500 mb-1">Title *</label>
                    <Input
                      type="text"
                      placeholder="e.g., Match vs Chelsea Academy"
                      value={newVideo.title}
                      onChange={(e) => setNewVideo({ ...newVideo, title: e.target.value })}
                      className="bg-zinc-800 border-zinc-700 text-white text-sm placeholder:text-zinc-600"
                      data-testid="video-title-input"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs text-zinc-500 mb-1">Date Recorded</label>
                    <Input
                      type="date"
                      value={newVideo.date_recorded}
                      onChange={(e) => setNewVideo({ ...newVideo, date_recorded: e.target.value })}
                      className="bg-zinc-800 border-zinc-700 text-white text-sm"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Video URL *</label>
                  <div className="relative">
                    <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                    <Input
                      type="url"
                      placeholder="https://cloud.xbotgo.net/video/play?docCode=..."
                      value={newVideo.url}
                      onChange={(e) => setNewVideo({ ...newVideo, url: e.target.value })}
                      className="pl-10 bg-zinc-800 border-zinc-700 text-white text-sm placeholder:text-zinc-600"
                      data-testid="video-url-input"
                    />
                  </div>
                  <p className="text-xs text-zinc-600 mt-1">
                    Supports: YouTube, VEO, XbotGo, Vimeo, Hudl, and other video links
                  </p>
                </div>
                
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Description</label>
                  <Input
                    type="text"
                    placeholder="Optional description..."
                    value={newVideo.description}
                    onChange={(e) => setNewVideo({ ...newVideo, description: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-white text-sm placeholder:text-zinc-600"
                  />
                </div>
                
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Tags</label>
                  <div className="relative">
                    <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                    <Input
                      type="text"
                      placeholder="match, training, goals (comma separated)"
                      value={newVideo.tags}
                      onChange={(e) => setNewVideo({ ...newVideo, tags: e.target.value })}
                      className="pl-10 bg-zinc-800 border-zinc-700 text-white text-sm placeholder:text-zinc-600"
                    />
                  </div>
                </div>
                
                {/* Match Details Toggle */}
                <div className="pt-2 border-t border-zinc-700">
                  <button
                    type="button"
                    onClick={() => setShowMatchDetails(!showMatchDetails)}
                    className="flex items-center gap-2 text-sm text-[#02b5ff] hover:text-[#02b5ff]/80"
                  >
                    <Trophy className="h-4 w-4" />
                    {showMatchDetails ? 'Hide Match Details' : 'Add Match Details (Score, Stats)'}
                    {showMatchDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>
                </div>
                
                {/* Match Details Form */}
                {showMatchDetails && (
                  <div className="p-3 bg-zinc-800/50 border border-zinc-700 rounded-lg space-y-3">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs text-zinc-500 mb-1">Opponent</label>
                        <Input
                          type="text"
                          placeholder="e.g., Chelsea Academy"
                          value={matchDetails.opponent}
                          onChange={(e) => setMatchDetails({ ...matchDetails, opponent: e.target.value })}
                          className="bg-zinc-900 border-zinc-700 text-white text-sm placeholder:text-zinc-600"
                        />
                      </div>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <label className="block text-xs text-zinc-500 mb-1">Score (Us)</label>
                          <Input
                            type="number"
                            min="0"
                            placeholder="0"
                            value={matchDetails.score_for}
                            onChange={(e) => setMatchDetails({ ...matchDetails, score_for: e.target.value })}
                            className="bg-zinc-900 border-zinc-700 text-white text-sm"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs text-zinc-500 mb-1">Score (Them)</label>
                          <Input
                            type="number"
                            min="0"
                            placeholder="0"
                            value={matchDetails.score_against}
                            onChange={(e) => setMatchDetails({ ...matchDetails, score_against: e.target.value })}
                            className="bg-zinc-900 border-zinc-700 text-white text-sm"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-zinc-500 mb-1">Result</label>
                        <select
                          value={matchDetails.result}
                          onChange={(e) => setMatchDetails({ ...matchDetails, result: e.target.value })}
                          className="w-full p-2 bg-zinc-900 border border-zinc-700 text-white text-sm rounded"
                        >
                          <option value="">Select...</option>
                          <option value="win">Win</option>
                          <option value="draw">Draw</option>
                          <option value="loss">Loss</option>
                        </select>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-zinc-500 mb-1">Venue</label>
                        <Input
                          type="text"
                          placeholder="e.g., Home / Away / Training Ground"
                          value={matchDetails.venue}
                          onChange={(e) => setMatchDetails({ ...matchDetails, venue: e.target.value })}
                          className="bg-zinc-900 border-zinc-700 text-white text-sm placeholder:text-zinc-600"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-zinc-500 mb-1">Competition</label>
                        <select
                          value={matchDetails.competition}
                          onChange={(e) => setMatchDetails({ ...matchDetails, competition: e.target.value })}
                          className="w-full p-2 bg-zinc-900 border border-zinc-700 text-white text-sm rounded"
                        >
                          <option value="">Select...</option>
                          <option value="league">League</option>
                          <option value="cup">Cup</option>
                          <option value="friendly">Friendly</option>
                          <option value="tournament">Tournament</option>
                          <option value="training_match">Training Match</option>
                          <option value="trial">Trial</option>
                        </select>
                      </div>
                    </div>
                    
                    {/* Player Stats */}
                    <div className="pt-3 border-t border-zinc-700">
                      <label className="block text-xs text-zinc-500 mb-2 uppercase">Player Stats</label>
                      
                      {/* Added stats */}
                      {playerStats.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                          {playerStats.map((stat, idx) => (
                            <div 
                              key={idx}
                              className="flex items-center gap-2 px-2 py-1 bg-zinc-700 rounded text-sm"
                            >
                              <span>{STAT_ICONS[stat.category] || '📝'}</span>
                              <span className="text-white">{stat.stat_label}</span>
                              {stat.value !== '1' && <span className="text-zinc-400">x{stat.value}</span>}
                              {stat.minute && <span className="text-zinc-500 text-xs">{stat.minute}'</span>}
                              <button
                                onClick={() => handleRemoveStat(idx)}
                                className="text-zinc-400 hover:text-red-400"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Add stat form */}
                      <div className="flex gap-2">
                        <select
                          value={newStat.stat_type}
                          onChange={(e) => setNewStat({ ...newStat, stat_type: e.target.value })}
                          className="flex-1 p-2 bg-zinc-900 border border-zinc-700 text-white text-sm rounded"
                        >
                          <option value="">Select stat...</option>
                          {statTypes.map((stat) => (
                            <option key={stat.id} value={stat.id}>
                              {stat.label} ({stat.category})
                            </option>
                          ))}
                        </select>
                        <Input
                          type="number"
                          min="1"
                          placeholder="Qty"
                          value={newStat.value}
                          onChange={(e) => setNewStat({ ...newStat, value: e.target.value })}
                          className="w-16 bg-zinc-900 border-zinc-700 text-white text-sm"
                        />
                        <Input
                          type="text"
                          placeholder="Min'"
                          value={newStat.minute}
                          onChange={(e) => setNewStat({ ...newStat, minute: e.target.value })}
                          className="w-16 bg-zinc-900 border-zinc-700 text-white text-sm"
                        />
                        <Button
                          type="button"
                          onClick={handleAddStat}
                          disabled={!newStat.stat_type}
                          className="h-9 px-3 bg-zinc-700 hover:bg-zinc-600 text-white text-xs disabled:opacity-50"
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="flex gap-2 pt-2">
                  <Button
                    onClick={handleAddVideo}
                    disabled={adding}
                    className="h-9 px-4 bg-green-600 hover:bg-green-500 text-white text-xs"
                    data-testid="confirm-add-video"
                  >
                    {adding ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-1" />
                    ) : (
                      <Plus className="h-4 w-4 mr-1" />
                    )}
                    Add Video
                  </Button>
                  <Button
                    onClick={() => setShowAddForm(false)}
                    variant="outline"
                    className="h-9 px-4 border-zinc-700 text-zinc-400 text-xs"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Video Player Modal */}
          {playingVideo && (
            <div className="mb-6 bg-zinc-900 border border-zinc-700 rounded-lg overflow-hidden" data-testid="video-player">
              <div className="flex items-center justify-between p-3 bg-zinc-800 border-b border-zinc-700">
                <div className="flex items-center gap-2">
                  <Play className="h-4 w-4 text-green-400" />
                  <span className="text-sm font-medium text-white">{playingVideo.title}</span>
                  <span 
                    className="px-2 py-0.5 text-xs rounded"
                    style={{ 
                      backgroundColor: `${getSourceConfig(playingVideo.source).color}20`,
                      color: getSourceConfig(playingVideo.source).color
                    }}
                  >
                    {playingVideo.source}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={playingVideo.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1.5 hover:bg-zinc-700 rounded text-zinc-400 hover:text-white"
                    title="Open in new tab"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                  <button
                    onClick={() => setPlayingVideo(null)}
                    className="p-1.5 hover:bg-red-500/20 rounded text-zinc-400 hover:text-red-400"
                    title="Close"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="aspect-video bg-black">
                <iframe
                  ref={iframeRef}
                  src={playingVideo.embed_url}
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                  allowFullScreen
                  className="w-full h-full"
                  title={playingVideo.title}
                />
              </div>
              
              {/* Timestamp Markers Section */}
              <div className="p-3 border-t border-zinc-700">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Bookmark className="h-4 w-4 text-[#02b5ff]" />
                    <span className="text-xs font-bold text-zinc-400 uppercase">Timestamp Markers</span>
                    {playingVideo.timestamp_markers?.length > 0 && (
                      <span className="px-1.5 py-0.5 bg-zinc-700 text-zinc-300 text-xs rounded">
                        {playingVideo.timestamp_markers.length}
                      </span>
                    )}
                  </div>
                  {canEdit && (
                    <Button
                      onClick={() => setShowMarkerForm(!showMarkerForm)}
                      size="sm"
                      className="h-7 px-2 bg-[#02b5ff]/20 hover:bg-[#02b5ff]/30 text-[#02b5ff] text-xs"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add Marker
                    </Button>
                  )}
                </div>
                
                {/* Add Marker Form */}
                {showMarkerForm && (
                  <div className="mb-3 p-3 bg-zinc-800/50 border border-zinc-700 rounded-lg">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
                      <div className="flex gap-1">
                        <Input
                          type="number"
                          min="0"
                          placeholder="Min"
                          value={newMarker.minutes}
                          onChange={(e) => setNewMarker({ ...newMarker, minutes: e.target.value })}
                          className="bg-zinc-900 border-zinc-700 text-white text-sm h-8"
                        />
                        <Input
                          type="number"
                          min="0"
                          max="59"
                          placeholder="Sec"
                          value={newMarker.seconds}
                          onChange={(e) => setNewMarker({ ...newMarker, seconds: e.target.value })}
                          className="bg-zinc-900 border-zinc-700 text-white text-sm h-8"
                        />
                      </div>
                      <Input
                        type="text"
                        placeholder="Label (e.g., Goal)"
                        value={newMarker.label}
                        onChange={(e) => setNewMarker({ ...newMarker, label: e.target.value })}
                        className="bg-zinc-900 border-zinc-700 text-white text-sm h-8"
                      />
                      <select
                        value={newMarker.stat_type}
                        onChange={(e) => setNewMarker({ ...newMarker, stat_type: e.target.value })}
                        className="bg-zinc-900 border border-zinc-700 text-white text-sm rounded h-8 px-2"
                      >
                        <option value="">Type (optional)</option>
                        {statTypes.slice(0, 15).map((stat) => (
                          <option key={stat.id} value={stat.id}>{stat.label}</option>
                        ))}
                      </select>
                      <div className="flex gap-1">
                        <Button
                          onClick={handleAddMarker}
                          disabled={addingMarker}
                          className="h-8 px-3 bg-green-600 hover:bg-green-500 text-white text-xs flex-1"
                        >
                          {addingMarker ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Add'}
                        </Button>
                        <Button
                          onClick={() => setShowMarkerForm(false)}
                          variant="outline"
                          className="h-8 px-2 border-zinc-700 text-zinc-400"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    <Input
                      type="text"
                      placeholder="Notes (optional)"
                      value={newMarker.notes}
                      onChange={(e) => setNewMarker({ ...newMarker, notes: e.target.value })}
                      className="bg-zinc-900 border-zinc-700 text-white text-sm h-8"
                    />
                  </div>
                )}
                
                {/* Markers List */}
                {playingVideo.timestamp_markers?.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {playingVideo.timestamp_markers
                      .sort((a, b) => a.time_seconds - b.time_seconds)
                      .map((marker) => (
                        <div 
                          key={marker.id}
                          className="flex items-center gap-1 px-2 py-1 bg-zinc-800 hover:bg-zinc-700 rounded cursor-pointer group transition-colors"
                          onClick={() => handleJumpToTimestamp(marker.time_seconds)}
                          title={marker.notes || `Jump to ${formatTimestamp(marker.time_seconds)}`}
                        >
                          <SkipForward className="h-3 w-3 text-[#02b5ff]" />
                          <span className="text-xs font-mono text-[#02b5ff]">
                            {formatTimestamp(marker.time_seconds)}
                          </span>
                          <span className="text-xs text-white">{marker.label}</span>
                          {marker.stat_type && (
                            <span className="text-xs">
                              {STAT_ICONS[statTypes.find(s => s.id === marker.stat_type)?.category] || '📝'}
                            </span>
                          )}
                          {canEdit && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteMarker(marker.id);
                              }}
                              className="opacity-0 group-hover:opacity-100 ml-1 text-zinc-500 hover:text-red-400"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      ))}
                  </div>
                ) : (
                  <p className="text-xs text-zinc-500 text-center py-2">
                    No markers yet. Add timestamps to jump to key moments.
                  </p>
                )}
                
                {/* Clip Export Button */}
                {playingVideo.timestamp_markers?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-zinc-700">
                    <Button
                      onClick={handleLoadClips}
                      disabled={loadingClips}
                      className="w-full h-8 bg-purple-600/20 hover:bg-purple-600/30 text-purple-400 text-xs"
                    >
                      {loadingClips ? (
                        <Loader2 className="h-3 w-3 animate-spin mr-2" />
                      ) : (
                        <Scissors className="h-3 w-3 mr-2" />
                      )}
                      Export Clips ({playingVideo.timestamp_markers.length})
                    </Button>
                  </div>
                )}
                
                {/* Clips Export Panel */}
                {showClipsPanel && clips.length > 0 && (
                  <div className="mt-3 p-3 bg-purple-900/20 border border-purple-700/30 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Scissors className="h-4 w-4 text-purple-400" />
                        <span className="text-xs font-bold text-purple-400 uppercase">Video Clips</span>
                      </div>
                      <button
                        onClick={() => setShowClipsPanel(false)}
                        className="text-zinc-500 hover:text-white"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    <p className="text-xs text-zinc-500 mb-3">
                      Click to copy shareable clip URLs (±5 sec padding around each marker)
                    </p>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {clips.map((clip) => (
                        <div 
                          key={clip.marker_id}
                          className="flex items-center justify-between p-2 bg-zinc-800/50 rounded hover:bg-zinc-800 cursor-pointer group"
                          onClick={() => handleCopyClipUrl(clip)}
                        >
                          <div className="flex items-center gap-3">
                            <span className="w-6 h-6 bg-purple-600/30 rounded-full flex items-center justify-center text-xs text-purple-400">
                              {clip.clip_number}
                            </span>
                            <div>
                              <div className="text-sm text-white">{clip.label}</div>
                              <div className="text-xs text-zinc-500">
                                {clip.time_formatted.start} → {clip.time_formatted.end} ({clip.duration}s)
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {clip.clip_url ? (
                              copiedClipId === clip.marker_id ? (
                                <Check className="h-4 w-4 text-green-400" />
                              ) : (
                                <Copy className="h-4 w-4 text-zinc-500 group-hover:text-white" />
                              )
                            ) : (
                              <span className="text-xs text-zinc-600">No URL</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* AI Video Analysis Section */}
                <div className="mt-3 pt-3 border-t border-zinc-700">
                  <Button
                    onClick={() => setShowAiPanel(!showAiPanel)}
                    className="w-full h-8 bg-gradient-to-r from-[#02b5ff]/20 to-[#ff025f]/20 hover:from-[#02b5ff]/30 hover:to-[#ff025f]/30 text-white text-xs"
                    data-testid="ai-analysis-toggle"
                  >
                    <Sparkles className="h-3 w-3 mr-2" />
                    AI Event Detection
                    {showAiPanel ? <ChevronUp className="h-3 w-3 ml-2" /> : <ChevronDown className="h-3 w-3 ml-2" />}
                  </Button>
                </div>
                
                {/* AI Analysis Panel */}
                {showAiPanel && (
                  <div className="mt-3 p-4 bg-gradient-to-br from-[#02b5ff]/10 to-[#ff025f]/10 border border-[#02b5ff]/30 rounded-lg">
                    <div className="flex items-center gap-2 mb-4">
                      <Sparkles className="h-5 w-5 text-[#02b5ff]" />
                      <h4 className="text-sm font-bold text-white">AI-Powered Event Detection</h4>
                    </div>
                    
                    <p className="text-xs text-zinc-400 mb-4">
                      Use AI vision to automatically detect key moments like goals, saves, tackles, and more based on your selected sport.
                    </p>
                    
                    {/* Sport Selection */}
                    <div className="mb-4">
                      <label className="block text-xs text-zinc-500 mb-2 uppercase">Select Sport</label>
                      <div className="grid grid-cols-3 gap-2">
                        {AI_SUPPORTED_SPORTS.map((sport) => (
                          <button
                            key={sport.id}
                            onClick={() => setAiSport(sport.id)}
                            className={`p-2 rounded-lg text-xs font-medium transition-all ${
                              aiSport === sport.id 
                                ? 'bg-[#02b5ff] text-white shadow-lg shadow-[#02b5ff]/30' 
                                : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-white'
                            }`}
                            data-testid={`sport-select-${sport.id}`}
                          >
                            <span className="text-lg block mb-1">{sport.icon}</span>
                            {sport.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Detected Events for Selected Sport */}
                    <div className="mb-4 p-3 bg-zinc-800/50 rounded-lg">
                      <p className="text-xs text-zinc-500 mb-2">Events AI will detect:</p>
                      <div className="flex flex-wrap gap-1">
                        {aiSport === 'football' && ['Goal', 'Shot', 'Save', 'Tackle', 'Dribble', 'Foul', 'Corner'].map(e => (
                          <span key={e} className="px-2 py-0.5 bg-zinc-700 text-zinc-300 text-xs rounded">{e}</span>
                        ))}
                        {aiSport === 'rugby' && ['Try', 'Conversion', 'Tackle', 'Ruck', 'Maul', 'Lineout'].map(e => (
                          <span key={e} className="px-2 py-0.5 bg-zinc-700 text-zinc-300 text-xs rounded">{e}</span>
                        ))}
                        {aiSport === 'basketball' && ['Basket', '3-Pointer', 'Dunk', 'Block', 'Steal', 'Assist'].map(e => (
                          <span key={e} className="px-2 py-0.5 bg-zinc-700 text-zinc-300 text-xs rounded">{e}</span>
                        ))}
                        {aiSport === 'tennis' && ['Ace', 'Winner', 'Volley', 'Smash', 'Rally'].map(e => (
                          <span key={e} className="px-2 py-0.5 bg-zinc-700 text-zinc-300 text-xs rounded">{e}</span>
                        ))}
                        {aiSport === 'hockey' && ['Goal', 'Save', 'Assist', 'Penalty', 'Power Play'].map(e => (
                          <span key={e} className="px-2 py-0.5 bg-zinc-700 text-zinc-300 text-xs rounded">{e}</span>
                        ))}
                        {aiSport === 'generic' && ['Score', 'Save', 'Tackle', 'Foul', 'Key Moment'].map(e => (
                          <span key={e} className="px-2 py-0.5 bg-zinc-700 text-zinc-300 text-xs rounded">{e}</span>
                        ))}
                      </div>
                    </div>
                    
                    {/* Analysis Progress */}
                    {aiAnalyzing && (
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-zinc-400">Analyzing video...</span>
                          <span className="text-xs text-[#02b5ff]">{aiProgress}%</span>
                        </div>
                        <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-[#02b5ff] to-[#ff025f] transition-all duration-300"
                            style={{ width: `${aiProgress}%` }}
                          />
                        </div>
                      </div>
                    )}
                    
                    {/* AI Results */}
                    {aiResults && (
                      <div className="mb-4 p-3 bg-green-900/20 border border-green-600/30 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Check className="h-4 w-4 text-green-400" />
                          <span className="text-sm font-medium text-green-400">
                            Found {aiResults.high_confidence_events} Key Moments
                          </span>
                        </div>
                        <div className="space-y-2 max-h-32 overflow-y-auto">
                          {aiResults.suggested_markers?.slice(0, 5).map((marker, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-xs">
                              <span className="font-mono text-[#02b5ff]">
                                {Math.floor(marker.time_seconds / 60)}:{(marker.time_seconds % 60).toString().padStart(2, '0')}
                              </span>
                              <span className="text-white">{marker.label}</span>
                              {marker.notes && (
                                <span className="text-zinc-500 truncate">{marker.notes}</span>
                              )}
                            </div>
                          ))}
                        </div>
                        <Button
                          onClick={handleApplyAiMarkers}
                          className="w-full mt-3 h-8 bg-green-600 hover:bg-green-500 text-white text-xs"
                        >
                          <Plus className="h-3 w-3 mr-2" />
                          Add All Markers to Video
                        </Button>
                      </div>
                    )}
                    
                    {/* Analyze Button */}
                    {!aiResults && (
                      <Button
                        onClick={handleAiAnalysis}
                        disabled={aiAnalyzing}
                        className="w-full h-10 bg-gradient-to-r from-[#02b5ff] to-[#ff025f] hover:from-[#02b5ff]/90 hover:to-[#ff025f]/90 text-white font-semibold"
                        data-testid="start-ai-analysis"
                      >
                        {aiAnalyzing ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Analyzing...
                          </>
                        ) : (
                          <>
                            <Sparkles className="h-4 w-4 mr-2" />
                            Analyze Video with AI
                          </>
                        )}
                      </Button>
                    )}
                    
                    <p className="text-xs text-zinc-600 mt-3 text-center">
                      Powered by GPT-4o Vision • Analyzes key frames
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Videos List */}
          {videos.length === 0 ? (
            <div className="text-center py-12 border-t border-zinc-800" data-testid="no-videos">
              <Video className="h-12 w-12 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500 text-sm mb-2">No video footage added yet</p>
              <p className="text-zinc-600 text-xs">
                Add links to match recordings, training sessions, and highlights
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="videos-grid">
              {videos.map((video) => {
                const sourceConfig = getSourceConfig(video.source);
                
                // Generate thumbnail URL based on video source
                const getThumbnailUrl = () => {
                  if (video.thumbnail_url) return video.thumbnail_url;
                  
                  const url = video.url || '';
                  
                  // YouTube thumbnail
                  if (video.source === 'YouTube') {
                    const youtubeId = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/)?.[1];
                    if (youtubeId) return `https://img.youtube.com/vi/${youtubeId}/mqdefault.jpg`;
                  }
                  
                  // Vimeo - would need API call, return null for now
                  if (video.source === 'Vimeo') {
                    const vimeoId = url.match(/vimeo\.com\/(\d+)/)?.[1];
                    if (vimeoId) return `https://vumbnail.com/${vimeoId}.jpg`;
                  }
                  
                  return null;
                };
                
                const thumbnailUrl = getThumbnailUrl();
                
                return (
                  <div 
                    key={video.id}
                    className="bg-zinc-900/50 border border-zinc-800 rounded-lg overflow-hidden hover:border-zinc-700 transition-colors"
                    data-testid={`video-card-${video.id}`}
                  >
                    {/* Video Thumbnail/Preview */}
                    <div 
                      className="aspect-video bg-zinc-800 relative cursor-pointer group overflow-hidden"
                      onClick={() => handlePlayVideo(video)}
                    >
                      {thumbnailUrl ? (
                        <img 
                          src={thumbnailUrl}
                          alt={video.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div 
                        className={`absolute inset-0 flex items-center justify-center bg-gradient-to-t from-black/80 via-black/40 to-transparent ${thumbnailUrl ? '' : 'bg-zinc-800'}`}
                        style={{ display: thumbnailUrl ? 'flex' : 'flex' }}
                      >
                        <div 
                          className="w-14 h-14 rounded-full flex items-center justify-center text-2xl bg-black/60 group-hover:bg-black/40 group-hover:scale-110 transition-all duration-300 shadow-lg"
                        >
                          <Play className="h-6 w-6 text-white ml-1" />
                        </div>
                      </div>
                      <div 
                        className="absolute top-2 right-2 px-2 py-1 rounded text-xs font-bold shadow-lg"
                        style={{ 
                          backgroundColor: `${sourceConfig.color}`,
                          color: 'white'
                        }}
                      >
                        {video.source}
                      </div>
                      {/* Duration indicator if available */}
                      {video.duration_seconds && (
                        <div className="absolute bottom-2 right-2 px-1.5 py-0.5 bg-black/80 rounded text-xs text-white font-mono">
                          {Math.floor(video.duration_seconds / 60)}:{(video.duration_seconds % 60).toString().padStart(2, '0')}
                        </div>
                      )}
                      {/* Marker count indicator */}
                      {video.timestamp_markers?.length > 0 && (
                        <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-[#02b5ff]/90 rounded-full text-xs text-white flex items-center gap-1">
                          <Bookmark className="h-3 w-3" />
                          {video.timestamp_markers.length}
                        </div>
                      )}
                    </div>
                    
                    {/* Video Info */}
                    <div className="p-3">
                      <h4 className="text-white font-medium text-sm mb-1 line-clamp-1">
                        {video.title}
                      </h4>
                      
                      {/* Match Details Display */}
                      {video.match_details && (
                        <div className="mb-2 p-2 bg-zinc-800/50 rounded">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-zinc-400">vs {video.match_details.opponent}</span>
                            {video.match_details.score && (
                              <span className={`font-bold ${
                                video.match_details.result === 'win' ? 'text-green-400' :
                                video.match_details.result === 'loss' ? 'text-red-400' :
                                'text-yellow-400'
                              }`}>
                                {video.match_details.score}
                              </span>
                            )}
                          </div>
                          {video.match_details.competition && (
                            <span className="text-xs text-zinc-500 capitalize">{video.match_details.competition.replace('_', ' ')}</span>
                          )}
                        </div>
                      )}
                      
                      {/* Player Stats Display */}
                      {video.player_stats && video.player_stats.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {video.player_stats.slice(0, 4).map((stat, idx) => (
                            <span 
                              key={idx}
                              className="px-1.5 py-0.5 bg-[#02b5ff]/20 text-[#02b5ff] text-xs rounded flex items-center gap-1"
                              title={stat.minute ? `${stat.minute}'` : ''}
                            >
                              {STAT_ICONS[stat.category] || '📝'}
                              {stat.stat_label}
                              {stat.value !== '1' && stat.value !== 1 && ` x${stat.value}`}
                            </span>
                          ))}
                          {video.player_stats.length > 4 && (
                            <span className="px-1.5 py-0.5 bg-zinc-800 text-zinc-400 text-xs rounded">
                              +{video.player_stats.length - 4}
                            </span>
                          )}
                        </div>
                      )}
                      
                      {video.description && (
                        <p className="text-zinc-500 text-xs mb-2 line-clamp-2">
                          {video.description}
                        </p>
                      )}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {video.date_recorded && (
                            <span className="text-xs text-zinc-500">
                              {new Date(video.date_recorded).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric'
                              })}
                            </span>
                          )}
                          {video.tags && video.tags.length > 0 && (
                            <div className="flex gap-1">
                              {video.tags.slice(0, 2).map((tag, idx) => (
                                <span key={idx} className="px-1.5 py-0.5 bg-zinc-800 text-zinc-400 text-xs rounded">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handlePlayVideo(video)}
                            className="p-1.5 hover:bg-[#02b5ff]/20 rounded text-zinc-500 hover:text-[#02b5ff]"
                            title={video.can_embed ? 'Play video' : 'Open in new tab'}
                          >
                            {video.can_embed ? (
                              <Play className="h-4 w-4" />
                            ) : (
                              <ExternalLink className="h-4 w-4" />
                            )}
                          </button>
                          {canEdit && (
                            <button
                              onClick={() => handleDeleteVideo(video.id)}
                              className="p-1.5 hover:bg-red-500/20 rounded text-zinc-500 hover:text-red-400"
                              title="Delete video"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
